import { missedInboundCallReportSchema } from "@repo/core/models"
import { z } from "zod"

import { useAgentState } from "@/agent-state"
import { PaginationParams } from "@/components/pagination/pagination-state"
import { config } from "@/config/index"
import { useQueryAPI } from "@/hooks/query-api"
import { type SortOptions } from "@/hooks/table-sorting"

const missedInboundCallsResponseSchema = z.object({
  data: z.object({
    missedInboundCalls: z.array(missedInboundCallReportSchema),
    total: z.number(),
  }),
})

const missedInboundCallCountsResponseSchema = z.object({
  data: z.object({
    total: z.number(),
  }),
})

const refetchInterval = config.missedCallPollingInterval

function useFetchMissedInboundCalls({
  campaignIds,
  page,
  pageSize,
  sortOptions,
}: {
  campaignIds: string[]
  sortOptions: SortOptions
} & PaginationParams) {
  const { state } = useAgentState()
  const agentCampaigns = state.context.agentProfile?.campaigns ?? []
  const campaigns = campaignIds.length ? campaignIds : agentCampaigns
  const isOffline = state.matches("offline")

  return useQueryAPI({
    apiEndPoint: "/inbound-calls/missed-calls",
    params: {
      campaigns: campaigns.join(","),
      date_from: null,
      date_to: null,
      page: page,
      page_size: pageSize,
      sort_key: sortOptions?.id ?? null,
      sort_direction: sortOptions?.direction ?? null,
      output: "list",
    },
    schema: missedInboundCallsResponseSchema,
    options: {
      refetchInterval,
      gcTime: 0,
      enabled: campaigns.length > 0 && isOffline,
      refetchOnWindowFocus: false,
    },
  })
}

function useFetchMissedInboundCallCounts() {
  const { state } = useAgentState()
  const agentCampaigns = state.context.agentProfile?.campaigns ?? []
  const isOffline = state.matches("offline")

  return useQueryAPI({
    apiEndPoint: "/inbound-calls/missed-calls",
    params: {
      campaigns: agentCampaigns.join(","),
      output: "count",
    },
    schema: missedInboundCallCountsResponseSchema,
    options: {
      refetchInterval,
      gcTime: 0,
      enabled: agentCampaigns.length > 0 && isOffline,
      refetchOnWindowFocus: false,
    },
  })
}

export {
  missedInboundCallsResponseSchema,
  useFetchMissedInboundCallCounts,
  useFetchMissedInboundCalls,
}
